import React, { useState, useRef, useEffect } from 'react';
import { Share2, Facebook, Mail, MessageCircle, X } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const ShareButtons = ({ title, description, url }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const shareUrl = url || window.location.href;
  const shareText = `${title}${description ? ' — ' + description.substring(0, 100) : ''}`;

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const channels = [
    {
      name: 'Facebook',
      icon: <Facebook className="w-4 h-4" />,
      color: 'bg-[#1877F2] hover:bg-[#166FE5]',
      action: () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`, '_blank', 'width=600,height=400'),
    },
    {
      name: 'Pinterest',
      icon: <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/></svg>,
      color: 'bg-[#E60023] hover:bg-[#CC001F]',
      action: () => window.open(`https://pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl)}&description=${encodeURIComponent(shareText)}`, '_blank', 'width=600,height=400'),
    },
    {
      name: 'Email',
      icon: <Mail className="w-4 h-4" />,
      color: 'bg-slate-600 hover:bg-slate-700',
      action: () => window.open(`mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`Check out this course: ${title}\n\n${shareUrl}`)}`, '_self'),
    },
    {
      name: 'Text',
      icon: <MessageCircle className="w-4 h-4" />,
      color: 'bg-green-600 hover:bg-green-700',
      action: () => window.open(`sms:?body=${encodeURIComponent(`Check this out: ${title} ${shareUrl}`)}`, '_self'),
    },
    {
      name: 'Copy Link',
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>,
      color: 'bg-slate-500 hover:bg-slate-600',
      action: () => { navigator.clipboard.writeText(shareUrl); toast.success('Link copied!'); setOpen(false); },
    },
  ];

  return (
    <div className="relative inline-block" ref={ref}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(!open)}
        className="gap-2 text-white border-slate-500 hover:bg-slate-700 hover:text-white bg-slate-800"
        data-testid="share-button"
      >
        <Share2 className="w-4 h-4" /> Share
      </Button>

      {open && (
        <div className="absolute right-0 top-full mt-2 z-50 bg-white rounded-xl shadow-lg border border-slate-200 p-3 w-56 animate-in fade-in slide-in-from-top-2 duration-200" data-testid="share-dropdown">
          <div className="flex items-center justify-between mb-2 pb-2 border-b border-slate-100">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Share this course</span>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="space-y-1">
            {channels.map((ch) => (
              <button
                key={ch.name}
                onClick={() => { ch.action(); if (ch.name !== 'Copy Link') setOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-white text-sm font-medium transition-colors ${ch.color}`}
                data-testid={`share-${ch.name.toLowerCase().replace(' ', '-')}`}
              >
                {ch.icon}
                {ch.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ShareButtons;
